import React, { useEffect, Suspense, lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { loadBranding } from './services/branding';
const PublicPaymentPage = lazy(() => import('./pages/PublicPaymentPage'));
const PublicConsentPage = lazy(() => import('./pages/PublicConsentPage'));
const PublicFamilyConsentPage = lazy(() => import('./pages/PublicFamilyConsentPage'));

const ManifestSwitcher = () => {
  const location = useLocation();
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin');
    const link = document.querySelector('link[rel="manifest"]');
    if (link) {
      link.href = isAdmin ? '/admin-manifest.json' : '/manifest.json';
    }
  }, [location.pathname]);
  return null;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, direction: 'rtl', textAlign: 'center' }}>
          <h2 style={{ color: 'red' }}>حدث خطأ في تحميل الصفحة</h2>
          <p style={{ color: '#666', marginTop: 10 }}>{this.state.error?.message || 'Unknown error'}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} style={{ marginTop: 20, padding: '10px 20px', background: '#f97316', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            إعادة تحميل
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages — lazy-loaded so each route ships in its own JS chunk
// (initial load only downloads the bundle for the page being visited).
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DailyTasksPage = lazy(() => import('./pages/DailyTasksPage'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const MemberPerformancePage = lazy(() => import('./pages/MemberPerformancePage'));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage'));
const PublicRegistrationPage = lazy(() => import('./pages/PublicRegistrationPage'));
const RegistrationRequestsPage = lazy(() => import('./pages/RegistrationRequestsPage'));
const RegistrationJourneyPage = lazy(() => import('./pages/RegistrationJourneyPage'));
const StaffRegistrationPage = lazy(() => import('./pages/StaffRegistrationPage'));
const CampaignInquiriesPage = lazy(() => import('./pages/CampaignInquiriesPage'));
const LevelsPage = lazy(() => import('./pages/LevelsPage'));
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'));
const MemberCertificates = lazy(() => import('./pages/member-portal/MemberCertificates'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DataIntegrityPage = lazy(() => import('./pages/DataIntegrityPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const OperationPasswordsPage = lazy(() => import('./pages/OperationPasswordsPage'));
const BranchesPage = lazy(() => import('./pages/BranchesPage'));
const RentedVenuesPage = lazy(() => import('./pages/RentedVenuesPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const StorePage = lazy(() => import('./pages/StorePage'));
const AccountingPage = lazy(() => import('./pages/AccountingPage'));
const MyExpensesPage = lazy(() => import('./pages/MyExpensesPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const TodayAttendancePage = lazy(() => import('./pages/TodayAttendancePage'));
const ScannerStationPage = lazy(() => import('./pages/ScannerStationPage'));
const MarketersPage = lazy(() => import('./pages/MarketersPage'));
const MarketerPortalPage = lazy(() => import('./pages/MarketerPortalPage'));
const LevelsBoardPage = lazy(() => import('./pages/LevelsBoardPage'));
const BranchDisplayPage = lazy(() => import('./pages/BranchDisplayPage'));
const SchedulePage = lazy(() => import('./pages/SchedulePage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const DailyNewCardsPage = lazy(() => import('./pages/DailyNewCardsPage'));
const CoachRatingsPage = lazy(() => import('./pages/CoachRatingsPage'));
const CoachAttendancePage = lazy(() => import('./pages/CoachAttendancePage'));
const CoachNotesPage = lazy(() => import('./pages/CoachNotesPage'));
const CoachSalariesPage = lazy(() => import('./pages/CoachSalariesPage'));
const SupervisorsPage = lazy(() => import('./pages/SupervisorsPage'));
const AdvertisementsPage = lazy(() => import('./pages/AdvertisementsPage'));
const DailyVideosPage = lazy(() => import('./pages/DailyVideosPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const RenewalsPage = lazy(() => import('./pages/RenewalsPage'));
const RenewalsHistoryPage = lazy(() => import('./pages/RenewalsHistoryPage'));
const BackupPage = lazy(() => import('./pages/BackupPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const DailyLedgerPage = lazy(() => import('./pages/DailyLedgerPage'));
const RentalsPage = lazy(() => import('./pages/RentalsPage'));
const DayExtensionsPage = lazy(() => import('./pages/DayExtensionsPage'));
const WhatsAppPage = lazy(() => import('./pages/WhatsAppPage'));
const WhatsAppBulkPage = lazy(() => import('./pages/WhatsAppBulkPage'));
const TournamentsPage = lazy(() => import('./pages/TournamentsPage'));
const SocialPublisherPage = lazy(() => import('./pages/SocialPublisherPage'));
const SuperLogin = lazy(() => import('./pages/SuperLogin'));
const SuperTenants = lazy(() => import('./pages/SuperTenants'));
const SuperEmails = lazy(() => import('./pages/SuperEmails'));
const SuperPayment = lazy(() => import('./pages/SuperPayment'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const OpsAlertsPage = lazy(() => import('./pages/OpsAlertsPage'));
import ExpiredTenantGuard from './components/ExpiredTenantGuard';

const SuperGuard = ({ children }) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('super_token') : null;
  if (!token) {
    window.location.replace('/super/login');
    return null;
  }
  return children;
};

// Member Portal Pages
const MemberLogin = lazy(() => import('./pages/member-portal/MemberLogin'));
const MemberDashboard = lazy(() => import('./pages/member-portal/MemberDashboard'));
const MemberSubscriptions = lazy(() => import('./pages/member-portal/MemberSubscriptions'));
const MemberSchedule = lazy(() => import('./pages/member-portal/MemberSchedule'));
const MemberQRCard = lazy(() => import('./pages/member-portal/MemberQRCard'));
const MemberNotifications = lazy(() => import('./pages/member-portal/MemberNotifications'));
const MemberAttendance = lazy(() => import('./pages/member-portal/MemberAttendance'));
const MemberRateCoach = lazy(() => import('./pages/member-portal/MemberRateCoach'));
const MemberDailyVideos = lazy(() => import('./pages/member-portal/MemberDailyVideos'));
const MemberLoyalty = lazy(() => import('./pages/member-portal/MemberLoyalty'));
const MemberSupport = lazy(() => import('./pages/member-portal/MemberSupport'));
const MemberTournaments = lazy(() => import('./pages/member-portal/MemberTournaments'));
const CoachProfile = lazy(() => import('./pages/member-portal/CoachProfile'));
const MemberProfile = lazy(() => import('./pages/member-portal/MemberProfile'));
const CoachQRPage = lazy(() => import('./pages/CoachQRPage'));
const AcademyPickerPage = lazy(() => import('./pages/AcademyPickerPage'));

// Lightweight fallback shown while a page chunk is being fetched
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="spinner" />
  </div>
);

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, permission }) => {
  const { isAuthenticated, loading, user, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  // A member session must never expose a stale staff session left in the
  // same browser. Staff can explicitly sign in again from /login.
  if (localStorage.getItem('member_token')) {
    return <Navigate to="/member-dashboard" replace />;
  }
  
  if (!isAuthenticated || !localStorage.getItem('token')) {
    return <Navigate to="/login" replace />;
  }
  
  // Check permission if specified (string or array of accepted permissions)
  if (permission && !isAdmin) {
    const userPermissions = user?.permissions || [];
    const required = Array.isArray(permission) ? permission : [permission];
    if (!required.some(p => userPermissions.includes(p))) {
      return <Navigate to="/admin/unauthorized" replace />;
    }
  }
  
  return children;
};

// Get first allowed route based on user permissions
const getFirstAllowedRoute = (permissions) => {
  // Dedicated scanner-station user (scanner ONLY) → land straight on the locked kiosk.
  // Users who also have normal permissions keep their usual landing page.
  if (permissions.length === 1 && permissions[0] === 'scanner-station') {
    return '/admin/scanner-station';
  }
  const routeOrder = ['dashboard', 'schedule', 'attendance', 'coach-attendance', 'coach-notes', 'members', 'activities', 'levels', 'certificates', 'tournaments', 'invoices', 'marketers', 'store', 'accounting', 'reports', 'messages', 'settings'];
  for (const route of routeOrder) {
    if (permissions.includes(route)) {
      return `/admin/${route}`;
    }
  }
  return '/admin/schedule';
};

// Smart Redirect Component - redirects to appropriate page based on permissions
const SmartRedirect = () => {
  const { isAuthenticated, loading, user, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated || !localStorage.getItem('token')) {
    const memberToken = localStorage.getItem('member_token');
    if (memberToken) {
      return <Navigate to="/member-dashboard" replace />;
    }
    const isNativeApp = Capacitor.isNativePlatform() || !!(window.Capacitor?.isNativePlatform?.());
    if (isNativeApp) {
      return <Navigate to="/member-login" replace />;
    }
    return <LandingPage />;
  }
  
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  const userPermissions = user?.permissions || [];
  return <Navigate to={getFirstAllowedRoute(userPermissions)} replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated && localStorage.getItem('token')) {
    if (isAdmin) {
      return <Navigate to="/admin/dashboard" replace />;
    }
    const userPermissions = user?.permissions || [];
    return <Navigate to={getFirstAllowedRoute(userPermissions)} replace />;
  }
  
  return children;
};

const ACADEMY_BYPASS_PREFIXES = ['/academy-picker', '/super', '/admin', '/login', '/privacy', '/terms', '/refund-policy', '/signup', '/coach-qr', '/register', '/r', '/join', '/marketer', '/pay', '/consent', '/family-consent', '/certificate/verify'];

const AcademyGuard = ({ children }) => {
  const location = useLocation();
  const path = location.pathname || '/';
  const confirmed = typeof window !== 'undefined' && localStorage.getItem('academy_confirmed') === '1';
  const hasAdminToken = typeof window !== 'undefined' && !!(localStorage.getItem('token') || localStorage.getItem('super_token'));
  const isBypassed = ACADEMY_BYPASS_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  if (!confirmed && !isBypassed && !hasAdminToken) {
    const next = encodeURIComponent(path + (location.search || ''));
    return <Navigate to={`/academy-picker?next=${next}`} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <AcademyGuard>
    <Routes>
      <Route path="/academy-picker" element={<AcademyPickerPage />} />
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Privacy Policy - Public page */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />

      {/* Public self-signup */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/coach-qr/:coachId" element={<CoachQRPage />} />

      {/* Public per-branch self-registration */}
      {/* All-branches (e.g. social-media ad) link: visitor picks the branch */}
      <Route path="/register/:tenantSlug" element={<PublicRegistrationPage />} />
      <Route path="/register/:tenantSlug/:branchId" element={<PublicRegistrationPage />} />
      <Route path="/r/:branchSlug" element={<PublicRegistrationPage />} />
      <Route path="/r/:tenantSlug/:branchSlug" element={<PublicRegistrationPage />} />
      {/* Short, clean alias for the social-media link (auto-tagged source=social) */}
      <Route path="/join/:tenantSlug" element={<PublicRegistrationPage />} />

      {/* Private marketer self-service portal (the token is the credential) */}
      <Route path="/marketer/:tenantSlug/:token" element={<MarketerPortalPage />} />
      <Route path="/pay/:token" element={<PublicPaymentPage />} />
      <Route path="/consent/:token" element={<PublicConsentPage />} />
      <Route path="/family-consent/:token" element={<PublicFamilyConsentPage />} />

      {/* Super-Admin (control plane) — outside main auth/permissions */}
      <Route path="/super" element={<Navigate to="/super/tenants" replace />} />
      <Route path="/super/login" element={<SuperLogin />} />
      <Route path="/super/tenants" element={<SuperGuard><SuperTenants /></SuperGuard>} />
      <Route path="/super/emails" element={<SuperGuard><SuperEmails /></SuperGuard>} />
      <Route path="/super/payment" element={<SuperGuard><SuperPayment /></SuperGuard>} />
      
      {/* Root - smart redirect based on user role */}
      <Route path="/" element={<SmartRedirect />} />

      {/* Member Portal Routes */}
      <Route path="/member-login" element={<MemberLogin />} />
      <Route path="/member-dashboard" element={<MemberDashboard />} />
      <Route path="/member-certificates" element={<MemberCertificates />} />
      <Route path="/certificate/verify/:certificateId" element={<MemberCertificates verification />} />
      <Route path="/subscriptions" element={<MemberSubscriptions />} />
      <Route path="/member-schedule" element={<MemberSchedule />} />
      <Route path="/card" element={<MemberQRCard />} />
      <Route path="/notifications" element={<MemberNotifications />} />
      <Route path="/member-attendance" element={<MemberAttendance />} />
      <Route path="/rate-coach" element={<MemberRateCoach />} />
      <Route path="/videos" element={<MemberDailyVideos />} />
      <Route path="/loyalty-points" element={<MemberLoyalty />} />
      <Route path="/support" element={<MemberSupport />} />
      <Route path="/member-messages" element={<MemberNotifications />} />
      <Route path="/my-tournaments" element={<MemberTournaments />} />
      <Route path="/coach-profile/:coachId" element={<CoachProfile />} />
      <Route path="/member-profile" element={<MemberProfile />} />
      
      {/* Onboarding wizard — admin only, no Layout chrome */}
      <Route
        path="/admin/onboarding"
        element={
          <ProtectedRoute permission="settings">
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Protected Routes - under /admin prefix */}
      <Route path="/admin/tasks" element={<ProtectedRoute permission="dashboard"><DailyTasksPage /></ProtectedRoute>} />
      <Route 
        path="/admin/unauthorized" 
        element={
          <ProtectedRoute>
            <UnauthorizedPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute permission="dashboard">
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/members" 
        element={
          <ProtectedRoute permission="members">
            <MembersPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/performance" element={<ProtectedRoute permission="members"><MemberPerformancePage /></ProtectedRoute>} />
      <Route path="/admin/performance/:memberId" element={<ProtectedRoute permission="members"><MemberPerformancePage /></ProtectedRoute>} />
      <Route 
        path="/admin/activities" 
        element={
          <ProtectedRoute permission="activities">
            <ActivitiesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/registration-requests" 
        element={
          <ProtectedRoute permission="invoices">
            <RegistrationRequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/registration-journey/:requestId" element={<ProtectedRoute permission="invoices"><RegistrationJourneyPage /></ProtectedRoute>} />
      <Route path="/admin/registration-new" element={<ProtectedRoute permission="invoices"><StaffRegistrationPage /></ProtectedRoute>} />
      <Route
        path="/admin/campaign-inquiries"
        element={
          <ProtectedRoute permission="messages">
            <CampaignInquiriesPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/marketers" 
        element={
          <ProtectedRoute permission="marketers">
            <MarketersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/levels" 
        element={
          <ProtectedRoute permission="levels">
            <LevelsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/certificates" element={<ProtectedRoute permission="certificates"><CertificatesPage /></ProtectedRoute>} />
      <Route 
        path="/admin/invoices" 
        element={
          <ProtectedRoute permission="invoices">
            <InvoicesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/reports" 
        element={
          <ProtectedRoute permission="reports">
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/data-integrity" element={<ProtectedRoute permission="settings"><DataIntegrityPage /></ProtectedRoute>} />
      <Route 
        path="/admin/daily-ledger" 
        element={
          <ProtectedRoute permission="daily-ledger">
            <DailyLedgerPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/day-extensions" 
        element={
          <ProtectedRoute permission="day-extensions">
            <DayExtensionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/rentals" 
        element={
          <ProtectedRoute permission="rentals">
            <RentalsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/messages" element={<Navigate to="/admin/whatsapp" replace />} />
      <Route 
        path="/admin/settings" 
        element={
          <ProtectedRoute permission="settings">
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute permission="settings">
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operation-passwords"
        element={
          <ProtectedRoute permission="settings">
            <OperationPasswordsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/branches" 
        element={
          <ProtectedRoute permission="branches">
            <BranchesPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/rented-venues"
        element={
          <ProtectedRoute permission="rented-venues">
            <RentedVenuesPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute permission="users">
            <UsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/store" 
        element={
          <ProtectedRoute permission="store">
            <StorePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/accounting" 
        element={
          <ProtectedRoute permission={["accounting", "internal-expenses-approve"]}>
            <AccountingPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/my-expenses"
        element={
          <ProtectedRoute permission="internal-expenses-create">
            <MyExpensesPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/attendance" 
        element={
          <ProtectedRoute permission="attendance">
            <AttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/today-attendance" 
        element={
          <ProtectedRoute permission="attendance">
            <TodayAttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/scanner-station" 
        element={
          <ProtectedRoute permission="scanner-station">
            <ScannerStationPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/levels-board"
        element={
          <ProtectedRoute permission="attendance">
            <LevelsBoardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/branch-display"
        element={
          <ProtectedRoute permission="attendance">
            <BranchDisplayPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/daily-new-cards"
        element={
          <ProtectedRoute permission="member-card">
            <DailyNewCardsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/schedule" 
        element={
          <ProtectedRoute permission="schedule">
            <SchedulePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coach-ratings" 
        element={
          <ProtectedRoute permission="coach-ratings">
            <CoachRatingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coach-attendance" 
        element={
          <ProtectedRoute permission="coach-attendance">
            <CoachAttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coach-notes" 
        element={
          <ProtectedRoute permission="coach-notes">
            <CoachNotesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/coach-salaries" 
        element={
          <ProtectedRoute permission="salaries">
            <CoachSalariesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/supervisors" 
        element={
          <ProtectedRoute permission="coaches">
            <SupervisorsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/advertisements" 
        element={
          <ProtectedRoute permission="advertisements">
            <AdvertisementsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/daily-videos" 
        element={
          <ProtectedRoute permission="daily-videos">
            <DailyVideosPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/loyalty" 
        element={
          <ProtectedRoute permission="loyalty">
            <LoyaltyPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/renewals" 
        element={
          <ProtectedRoute permission="renewals">
            <RenewalsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/renewals/history"
        element={
          <ProtectedRoute permission="renewals">
            <RenewalsHistoryPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/backup" 
        element={
          <ProtectedRoute permission="backup">
            <BackupPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute permission="settings">
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ops-alerts"
        element={
          <ProtectedRoute permission="settings">
            <OpsAlertsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/push-notifications" element={<Navigate to="/admin/whatsapp" replace />} />
      <Route 
        path="/admin/whatsapp" 
        element={
          <ProtectedRoute permission="messages">
            <WhatsAppPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/whatsapp-bulk" 
        element={
          <ProtectedRoute permission="messages">
            <WhatsAppBulkPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/tournaments" 
        element={
          <ProtectedRoute permission="tournaments">
            <TournamentsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/social-publisher"
        element={
          <ProtectedRoute permission="social-publisher">
            <SocialPublisherPage />
          </ProtectedRoute>
        }
      />
      
      {/* Catch-all → landing/smart redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AcademyGuard>
    </Suspense>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ManifestSwitcher />
          <ErrorBoundary>
            <ExpiredTenantGuard>
              <AppRoutes />
            </ExpiredTenantGuard>
          </ErrorBoundary>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;

try { loadBranding(); } catch (e) {}
try {
  setInterval(() => { try { loadBranding(); } catch (e) {} }, 30 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      try { loadBranding(); } catch (e) {}
    }
  });
} catch (e) {}
